import { getCurrentUser as amplifyGetCurrentUser, signOut } from 'aws-amplify/auth';
import axios from "axios";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ApiSystem, User } from "../state/types";
import { normalizeSystemsData } from '../components/system/systemHelpers';

const PUBLIC_PATHS = ['/login', '/logout', '/resetPassword'];

export const useSessionState = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState<User | null>(null);
  const [originalUser, setOriginalUser] = useState<User | null>(null);
  const [systems, setSystems] = useState<ApiSystem[]>([]);
  const [selectedSystem, setSelectedSystem] = useState<ApiSystem | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const systemStorageKey = useMemo(() => {
    const u = user?.username || "anon";
    return `selectedSystem:${u}`;
  }, [user?.username]);

  const selectedSystemRef = useRef<ApiSystem | null>(null);

  useEffect(() => {
    selectedSystemRef.current = selectedSystem;
  }, [selectedSystem]);

  // This effect runs on app startup to check for an existing session
  useEffect(() => {
    const checkCurrentUser = async () => {
      try {
        const storedOriginalUser = localStorage.getItem('originalUser');
        const storedImpersonatedUser = localStorage.getItem('impersonatedUser');

        if (storedOriginalUser && storedImpersonatedUser) {
          setOriginalUser(JSON.parse(storedOriginalUser));
          setUser(JSON.parse(storedImpersonatedUser));
        } else {
          const { userId } = await amplifyGetCurrentUser();
          await loginUser(userId);
        }
      } catch (error) {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkCurrentUser();
  }, []);


  useEffect(() => {
    if (isLoading) {
      return;
    }

    const isPublicPath = PUBLIC_PATHS.some(path => location.pathname.startsWith(path));
    const isIframePath = location.pathname.toLowerCase().includes('iframe');

    if (!user && !isPublicPath && !isIframePath) {
      navigate('/login');
    }
  }, [user, isLoading, navigate, location.pathname]);

  const loginUser = async (userId: string) => {
    try {
      const url = `${process.env.REACT_APP_UMS_API_BASE_URL}/user/${userId}/login`;
      const headers = {
        Authorization: 'bwenvdev',
        'Content-Type': 'application/json',
      };
      const { data } = await axios.post(url, {}, { headers });

      const isAutoAdmin = (
        data.Role === 'Unknown' &&
        data.LastLogin === 'Never Logged In' &&
        data.Attributes.email?.endsWith('@blackwidowimaging.com')
      );

      const loggedInUser: User = {
        createdDate: data.UserCreateDate,
        email: data.Attributes.email,
        first_name: data.Attributes.given_name,
        last_name: data.Attributes.family_name,
        phone_number: data.Attributes.phone_number,
        role: isAutoAdmin ? "Admin" : data.Role,
        system_access: data.SystemAccess,
        username: data.Username,
      };

      setUser(loggedInUser);
    } catch (error) {
      console.error("Error logging in via context:", error);
      throw error;
    }
  };

  const logoutUser = async () => {
    try {
      await signOut();
      setUser(null);
      setOriginalUser(null);
      // Clear masquerade from localStorage on logout
      localStorage.removeItem('originalUser');
      localStorage.removeItem('impersonatedUser');
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  };

  const startMasquerade = (impersonatedUser: User) => {
    if (user && (user.role === 'Admin' || user.role === 'Support')) {
      setOriginalUser(user);
      setUser(impersonatedUser);
      localStorage.setItem('originalUser', JSON.stringify(user));
      localStorage.setItem('impersonatedUser', JSON.stringify(impersonatedUser));
    }
  };

  const stopMasquerade = () => {
    setUser(originalUser);
    setOriginalUser(null);
    localStorage.removeItem('originalUser');
    localStorage.removeItem('impersonatedUser');
  };

  // --- Systems Logic ---
  const fetchSystems = useCallback(async () => {
    if (!user) return; // Don't fetch if no user is logged in

    setIsLoading(true);
    try {
      const url = `${process.env.REACT_APP_UMS_API_BASE_URL}/system`;
      const headers = { Authorization: `bwenvdev` };
      const { data } = await axios.get(url, { headers });

      const normalizedData = normalizeSystemsData(data.systems);

      // Filter systems based on user access, unless they are an Admin
      const accessibleSystems = user.role === 'Admin'
        ? normalizedData
        : normalizedData.filter((s: ApiSystem) => user.system_access.includes(s.systemCode));

      setSystems(accessibleSystems);

      const savedCode = localStorage.getItem(systemStorageKey);
      const currentCode = selectedSystemRef.current?.systemCode

      const next =
        (currentCode && accessibleSystems.find(s => s.systemCode === currentCode)) ||
        (savedCode && accessibleSystems.find(s => s.systemCode === savedCode)) ||
        (accessibleSystems[0] ?? null);

      setSelectedSystem(next);

      if (next) localStorage.setItem(systemStorageKey, next.systemCode);
      else localStorage.removeItem(systemStorageKey);

    } catch (error) {
      console.error("Failed to fetch systems:", error);
      setSystems([]);
      setSelectedSystem(null);
    } finally {
      setIsLoading(false);
    }
  }, [user, systemStorageKey]); // Depend on user to refetch when user changes

  // Effect to fetch systems once the user is logged in
  useEffect(() => {
    if (user) {
      fetchSystems();
    }
  }, [user, fetchSystems]);

  const selectSystem = useCallback((sys: ApiSystem | null) => {
    setSelectedSystem(sys);
    const key = systemStorageKey;
    if (!key) return;
    if (sys) localStorage.setItem(key, sys.systemCode);
    else localStorage.removeItem(key);
  }, [systemStorageKey]);

  const clearVehicleNavStateAndNavigate = (path: string) => {
    console.log("clearing nav state")
    localStorage.removeItem('vehiclesNavState');
    navigate(path);
  };

  return {
    // User Session
    user,
    originalUser,
    isLoggedIn: !!user,
    isLoading,
    setIsLoading,
    loginUser,
    logoutUser,
    startMasquerade,
    stopMasquerade,
    handleNavigation: clearVehicleNavStateAndNavigate,

    // Systems storage
    systems,
    setSystems,
    selectedSystem,
    selectSystem,
    refetchSystems: fetchSystems
  };
};